import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const fmtUsd = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** Slice 3 — read-only conversion visibility with anomaly flags. */
export default function ConversionsTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");

  const load = useCallback(async (st) => {
    try {
      const { data: d } = await api.get("/admin/conversions", { params: { limit: 100, status: st || undefined } });
      setData(d);
    } catch { setData({ rows: [], total: 0, summary_24h: {} }); }
  }, []);

  useEffect(() => { load(""); }, [load]);

  const s = data?.summary_24h || {};
  return (
    <div data-testid="admin-conversions-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {[
          ["24h conversions", s.conversions],
          ["24h volume", s.cents != null ? fmtUsd(s.cents) : "—"],
          ["24h coins minted", s.coins?.toLocaleString()],
          ["24h failed", s.failed, s.failed > 0 && "text-rose-400"],
          ["Users at daily cap", s.users_at_daily_cap, s.users_at_daily_cap > 0 && "text-amber-400"],
        ].map(([label, value, tint]) => (
          <div key={label} className="card-surface p-3 text-center">
            <div className={`font-display text-lg ${tint || "text-white"}`}>{value ?? "—"}</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mb-3">
        {["", "completed", "failed"].map((st) => (
          <button key={st} type="button" data-testid={`admin-conv-filter-${st || "all"}`}
            onClick={() => { setStatus(st); load(st); }}
            className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${
              status === st ? "border-rose-500 text-rose-400" : "border-white/15 text-zinc-500 hover:text-white"}`}>
            {st || "all"}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        {data?.rows?.map((r) => (
          <div key={r.id} data-testid={`admin-conv-row-${r.id}`} className="card-surface px-4 py-3 flex items-center gap-3 text-sm">
            <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${
              r.status === "completed" ? "text-emerald-400 border-emerald-600/50" : "text-rose-400 border-rose-600/50"}`}>
              {r.status}
            </span>
            <div className="min-w-0 flex-1">
              <span className="text-white">@{r.handle || r.user_id.slice(0, 8)}</span>
              <span className="text-zinc-400"> · {fmtUsd(r.cents)} → {r.coins.toLocaleString()} 🐼</span>
              {r.error && <span className="text-rose-400 text-xs ml-2">{r.error}</span>}
            </div>
            {r.anomaly_flags?.map((f) => (
              <span key={f} className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/50 px-1.5 py-0.5">{f}</span>
            ))}
            <span className="text-xs text-zinc-500 shrink-0">{new Date(r.created_at).toLocaleString()}</span>
          </div>
        ))}
        {data && data.rows.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-zinc-500">No conversions yet.</div>
        )}
      </div>
    </div>
  );
}
